/**
 * M2M Client Service
 *
 * Element-scoped service that fetches and caches Auth0 M2M client
 * friendly names for display in the FGA Manager UI.
 *
 * @packageDocumentation
 */

import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, catchError, of, timeout } from 'rxjs';
import { M2MClient, M2MClientsResponse, M2MClientServiceConfig } from '../models/m2m-client.model';
import { LoggerService } from './logger.service';
import { sortByName } from '../utils/sorting';

/**
 * Service for fetching Auth0 M2M client friendly names.
 *
 * Element-scoped (not providedIn: 'root') to ensure each `<fga-manager>`
 * web component instance has isolated state.
 *
 * @example
 * ```typescript
 * @Component({ providers: [M2mClientService] })
 * export class MyComponent {
 *   private readonly m2mService = inject(M2mClientService);
 * }
 * ```
 */
@Injectable()
export class M2mClientService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);

  private config: M2MClientServiceConfig | null = null;

  /** In-memory cache: client_id → M2MClient */
  private readonly clientCache = new Map<string, M2MClient>();

  private readonly _clients = signal<M2MClient[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _configured = signal(false);

  readonly clients = this._clients.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly configured = this._configured.asReadonly();
  readonly clientCount = computed(() => this._clients().length);

  /**
   * Configure the service with API settings.
   */
  configure(config: M2MClientServiceConfig): void {
    this.config = config;
    this._configured.set(true);
  }

  /**
   * Look up an M2M client by client_id.
   *
   * @param clientId - Auth0 client ID to look up
   * @returns M2MClient if found, null otherwise
   */
  getClientById(clientId: string): M2MClient | null {
    return this.clientCache.get(clientId) ?? null;
  }

  /**
   * Get the friendly display name for a client_id.
   *
   * @param clientId - Auth0 client ID
   * @returns Friendly name or null if not found
   */
  getClientName(clientId: string): string | null {
    return this.clientCache.get(clientId)?.name ?? null;
  }

  /**
   * Get the description for a client_id.
   *
   * @param clientId - Auth0 client ID
   * @returns Description or null if not found
   */
  getClientDescription(clientId: string): string | null {
    return this.clientCache.get(clientId)?.description ?? null;
  }

  /**
   * Update the organisation ID on an already-configured service.
   */
  setOrganisationId(organisationId: string): void {
    if (this.config) {
      this.config = { ...this.config, organisationId };
    }
  }

  /**
   * Load M2M clients for the configured organisation.
   *
   * @returns Array of M2M clients
   */
  async loadClients(): Promise<M2MClient[]> {
    if (!this.config) {
      this.logger.warn('M2mClientService', 'Service not configured');
      return [];
    }

    if (!this.config.organisationId) {
      this.logger.warn('M2mClientService', 'Organisation ID not set');
      return [];
    }

    // Return cached if available
    if (this.clientCache.size > 0) {
      return this._clients();
    }

    this._loading.set(true);
    this._error.set(null);

    try {
      const clients = await this.fetchM2MClients();
      this._clients.set(clients);
      this._loading.set(false);
      return clients;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load M2M clients';
      this._error.set(message);
      this._loading.set(false);
      this.logger.error('M2mClientService', 'Failed to load M2M clients', { error: message });
      return [];
    }
  }

  /**
   * Clear all cached data and reset state.
   */
  clearCache(): void {
    this.clientCache.clear();
    this._clients.set([]);
    this._error.set(null);
  }

  /**
   * Fetch M2M clients via GET /clients with a Lucene query scoped to the
   * configured organisation.
   */
  private async fetchM2MClients(): Promise<M2MClient[]> {
    const config = this.config!;
    const apiBase = config.apiBase.replace(/\/+$/, '');
    const orgId = config.organisationId;

    const url = `${apiBase}/clients`;
    const params: Record<string, string> = {
      q: `client_grant.organization_id:${orgId}`,
      fields: 'client_id,name,description',
      include_fields: 'true',
    };

    const headers: Record<string, string> = {
      'Authorization': `Bearer ${config.accessToken}`,
      'Accept': 'application/json',
    };

    if (config.subscriptionKey) {
      headers['ocp-apim-subscription-key'] = config.subscriptionKey;
    }

    const response = await firstValueFrom(
      this.http.get<M2MClientsResponse | M2MClient[]>(url, { headers, params }).pipe(
        timeout(10000),
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
            this.logger.warn('M2mClientService', `Auth error ${error.status} fetching M2M clients`);
          }

          this._error.set(this.toLoadErrorMessage(error));
          return of(null);
        }),
      ),
    );

    if (!response) {
      return [];
    }

    // Handle both response shapes: { clients: [...] } or [...]
    const clientArray = Array.isArray(response) ? response : (response.clients ?? []);

    const clients: M2MClient[] = clientArray
      .filter((c): c is M2MClient => !!c.name && !!c.client_id)
      .map(c => ({
        client_id: c.client_id,
        name: c.name,
        description: c.description,
      }));
    const sortedClients = sortByName(clients);

    // Populate cache
    for (const client of sortedClients) {
      this.clientCache.set(client.client_id, client);
    }

    this.logger.debug('M2mClientService', `Loaded ${sortedClients.length} M2M clients for org ${orgId}`);
    return sortedClients;
  }

  private toLoadErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse || error instanceof Error) {
      return error.message;
    }

    return 'Failed to load M2M clients';
  }
}
